import { ArrowLeft, Check, ChevronRight, Lightbulb, RotateCcw, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { Quest, World } from "../types";

type LessonScreenProps = {
  world: World;
  completed: string[];
  hearts: number;
  onBack: () => void;
  onComplete: (id: string) => void;
  onWrong: () => void;
};

function MaskPuzzle({ selected, onToggle }: { selected: string[]; onToggle: (key: string) => void }) {
  return (
    <div className="mask-board" aria-label="Causal Mask 四乘四棋盘">
      {Array.from({ length: 16 }, (_, index) => {
        const row = Math.floor(index / 4);
        const col = index % 4;
        const key = `${row}-${col}`;
        return (
          <button
            type="button"
            key={key}
            className={selected.includes(key) ? "masked" : ""}
            onClick={() => onToggle(key)}
            aria-label={`第 ${row + 1} 行第 ${col + 1} 列${selected.includes(key) ? "已遮挡" : "未遮挡"}`}
          >
            {selected.includes(key) ? <X /> : <span>{row + 1}→{col + 1}</span>}
          </button>
        );
      })}
    </div>
  );
}

function QuestChallenge({
  quest,
  onSolved,
  onWrong,
}: {
  quest: Quest;
  onSolved: () => void;
  onWrong: () => void;
}) {
  const [choice, setChoice] = useState<number | null>(null);
  const [sequence, setSequence] = useState<string[]>([]);
  const [mask, setMask] = useState<string[]>([]);
  const [result, setResult] = useState<"idle" | "correct" | "wrong">("idle");

  useEffect(() => {
    setChoice(null);
    setSequence([]);
    setMask([]);
    setResult("idle");
  }, [quest.id]);

  const expectedMask = useMemo(() => {
    const cells: string[] = [];
    for (let row = 0; row < 4; row += 1) {
      for (let col = 0; col < 4; col += 1) if (col > row) cells.push(`${row}-${col}`);
    }
    return cells;
  }, []);

  function checkAnswer() {
    let correct = false;
    if (quest.kind === "quiz") correct = choice === quest.correct;
    if (quest.kind === "sequence") correct = JSON.stringify(sequence) === JSON.stringify(quest.order);
    if (quest.kind === "mask") correct = mask.length === expectedMask.length && expectedMask.every((cell) => mask.includes(cell));
    if (correct) {
      setResult("correct");
      onSolved();
    } else {
      setResult("wrong");
      onWrong();
    }
  }

  const canCheck = quest.kind === "quiz" ? choice !== null : quest.kind === "sequence" ? sequence.length === quest.pieces?.length : mask.length > 0;

  return (
    <div className="challenge-area">
      {quest.kind === "quiz" ? (
        <div className="answer-grid">
          {quest.options?.map((option, index) => (
            <button
              type="button"
              key={option}
              className={choice === index ? "selected" : ""}
              onClick={() => { setChoice(index); setResult("idle"); }}
            >
              <span>{String.fromCharCode(65 + index)}</span>{option}
            </button>
          ))}
        </div>
      ) : null}

      {quest.kind === "sequence" ? (
        <div className="sequence-puzzle">
          <div className="sequence-slots" aria-label="你的排列">
            {quest.pieces?.map((_, index) => (
              <button type="button" key={index} className={sequence[index] ? "filled" : ""} onClick={() => setSequence((current) => current.slice(0, index))}>
                {sequence[index] ?? `${index + 1}`}
              </button>
            ))}
          </div>
          <div className="piece-tray" aria-label="待选拼图块">
            {quest.pieces?.filter((piece) => !sequence.includes(piece)).map((piece) => (
              <button type="button" key={piece} onClick={() => { setSequence((current) => [...current, piece]); setResult("idle"); }}>
                {piece}
              </button>
            ))}
          </div>
          <button className="reset-puzzle" type="button" onClick={() => { setSequence([]); setResult("idle"); }}>
            <RotateCcw /> 重排
          </button>
        </div>
      ) : null}

      {quest.kind === "mask" ? (
        <MaskPuzzle
          selected={mask}
          onToggle={(key) => { setMask((current) => current.includes(key) ? current.filter((cell) => cell !== key) : [...current, key]); setResult("idle"); }}
        />
      ) : null}

      {result === "correct" ? (
        <div className="result-box success"><Check /> <div><strong>过关！</strong><p>{quest.explanation}</p></div></div>
      ) : null}
      {result === "wrong" ? (
        <div className="result-box error"><X /> <div><strong>这块积木还没对上</strong><p>{quest.intuition}</p></div></div>
      ) : null}

      {result !== "correct" ? (
        <button className="primary-action" type="button" disabled={!canCheck} onClick={checkAnswer}>
          检查答案 <Sparkles />
        </button>
      ) : null}
    </div>
  );
}

export function LessonScreen({ world, completed, hearts, onBack, onComplete, onWrong }: LessonScreenProps) {
  const firstOpen = Math.max(0, world.quests.findIndex((quest) => !completed.includes(quest.id)));
  const [questIndex, setQuestIndex] = useState(firstOpen === -1 ? 0 : firstOpen);
  const quest = world.quests[questIndex];
  const solved = completed.includes(quest.id);

  useEffect(() => {
    const next = world.quests.findIndex((item) => !completed.includes(item.id));
    setQuestIndex(next === -1 ? 0 : next);
  }, [world.id]);

  return (
    <main className="lesson-screen" style={{ "--world-color": world.color, "--world-accent": world.accent } as React.CSSProperties}>
      <div className="lesson-clouds" aria-hidden="true" />
      <header className="lesson-topbar">
        <button type="button" onClick={onBack} className="icon-button" aria-label="返回地图"><ArrowLeft /></button>
        <div>
          <small>世界 {world.number} · {world.shortName}</small>
          <h1>{world.name}</h1>
        </div>
        <span className="lesson-hearts">❤ {hearts}</span>
      </header>

      <div className="quest-rail" aria-label="关卡进度">
        {world.quests.map((item, index) => (
          <button
            type="button"
            key={item.id}
            className={`${index === questIndex ? "active" : ""} ${completed.includes(item.id) ? "done" : ""}`}
            onClick={() => setQuestIndex(index)}
            aria-label={`第 ${index + 1} 关 ${item.title}`}
          >
            {completed.includes(item.id) ? <Check /> : index + 1}
          </button>
        ))}
      </div>

      <section className="lesson-layout">
        <aside className="mentor-panel">
          <div className="mentor-avatar" aria-hidden="true">🤖</div>
          <small>小星教练</small>
          <h2>{quest.title}</h2>
          <p>{quest.story}</p>
          <div className="intuition-note">
            <Lightbulb aria-hidden="true" />
            <div><strong>小学生直觉</strong><p>{quest.intuition}</p></div>
          </div>
          <p className="source-note">知识锚点：{quest.sourcePages}</p>
        </aside>

        <article className="quest-console">
          <div className="quest-heading">
            <span>关卡 {String(questIndex + 1).padStart(2, "0")}</span>
            <h2>{quest.question}</h2>
          </div>
          <QuestChallenge quest={quest} onSolved={() => onComplete(quest.id)} onWrong={onWrong} />
          {solved ? (
            <div className="interview-tip">
              <strong>🎤 面试升级句</strong>
              <p>{quest.interviewTip}</p>
            </div>
          ) : null}
          {solved && questIndex < world.quests.length - 1 ? (
            <button className="next-quest" type="button" onClick={() => setQuestIndex((index) => index + 1)}>
              下一关 <ChevronRight />
            </button>
          ) : null}
        </article>
      </section>
    </main>
  );
}
